import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { SessionService } from '../../services/session.service';
import { AppProfileService } from '../../services/app-profile.service';
import { RoutineService } from '../../services/routine.service';
import { BrandService } from '../../../services/brand-config.service';
import {
  CATEGORIES,
  RoutineSchedule,
  ToolCategory,
  TOOLS,
} from '../shell.models';

@Component({
  selector: 'app-onboarding-shell',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './app-onboarding.component.html',
  styleUrl: './app-onboarding.component.scss',
})
export class AppOnboardingComponent implements OnInit {
  private router = inject(Router);
  private translation = inject(TranslationService);
  private session = inject(SessionService);
  private profileSvc = inject(AppProfileService);
  private routineSvc = inject(RoutineService);
  private brand = inject(BrandService);

  step = 1;
  busy = false;

  name = '';
  birthYear: number | null = null;
  contraception = '';
  cycleStatus = '';
  selected: Record<string, boolean> = {};

  years: number[] = [];
  contraOpts = ['yes', 'no', 'prefer-not'];
  cycleOpts = ['regular', 'irregular', 'stopped', 'unknown'];
  cats: ToolCategory[] = ['core', 'body', 'mind', 'sense'];
  tools = TOOLS;
  private readonly brandAccent = this.brand.config.isMarkable ? '#D1409C' : '#ff0083';

  ngOnInit(): void {
    this.name = this.session.session?.user?.fname || '';
    const now = new Date().getFullYear();
    for (let y = now - 18; y >= now - 70; y--) this.years.push(y);
    // pre-select sensible defaults (matches prototype OB_DEFAULTS)
    ['diary', 'breathe', 'water', 'brain', 'bone', 'sleepex'].forEach(
      (id) => (this.selected[id] = true),
    );
  }

  t(key: string, opts?: any): string {
    return this.translation.translate(key, opts);
  }

  catColor(cat: ToolCategory): string {
    if (cat === 'mind') return this.brandAccent;
    return CATEGORIES[cat].color;
  }
  toolColor(tool: { cat: ToolCategory; color: string }): string {
    return tool.cat === 'mind' ? this.brandAccent : tool.color;
  }
  toolsIn(cat: ToolCategory) {
    return this.tools.filter((t) => t.cat === cat);
  }
  toolName(id: string): string {
    return this.t('shell.tools.' + id + '.name');
  }

  toggleTool(id: string): void {
    this.selected[id] = !this.selected[id];
  }
  selectedCount(): number {
    return Object.values(this.selected).filter(Boolean).length;
  }
  selectAll(): void {
    this.tools.forEach((t) => (this.selected[t.id] = true));
  }

  next(): void {
    if (this.step < 4) this.step++;
  }

  private lang(): string {
    return this.translation.getCurrentLanguage();
  }

  private buildSchedule(): RoutineSchedule {
    const sched: RoutineSchedule = {};
    this.tools.forEach((t) => {
      if (this.selected[t.id]) {
        sched[t.id] = {
          active: true,
          freq: t.defaultFreq || 'daily',
          time: t.defaultTime || 'anytime',
        };
      }
    });
    return sched;
  }

  async finish(goToFirstTool: boolean): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.profileSvc.updateProfile({
        fname: this.name?.trim() || undefined,
        birthYear: this.birthYear,
        contraception: this.contraception || undefined,
        cycleStatus: this.cycleStatus || undefined,
        onboardingDone: true,
      });
      await this.routineSvc.saveSchedule(this.buildSchedule());
    } catch (e) {
      console.error('Onboarding save failed:', e);
    } finally {
      this.busy = false;
    }

    const firstTool = this.tools.find((t) => this.selected[t.id])?.id;
    if (goToFirstTool && firstTool) {
      this.router.navigate(['/' + this.lang() + '/app/tool/' + firstTool]);
    } else {
      this.router.navigate(['/' + this.lang() + '/app/home']);
    }
  }
}
